/**
 * TeamSystem Impact - User Management Page
 * Admin UI per visualizzare, modificare e disattivare utenti
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Switch,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  FormControlLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { userApi } from './api/user.api';
import { AddUserDialog } from './components/AddUserDialog';
import type { Utente, Ruolo } from '@teamsystem-impact/shared';
import { useAuthStore } from '../../store/authStore';

const RUOLI: Ruolo[] = ['amministratore', 'responsabile_esg', 'contributore', 'visualizzatore'];

function getRuoloForAzienda(utente: Utente, aziendaId: string): Ruolo | null {
  const ruolo = utente.ruoli?.find(r => r.aziendaId === aziendaId);
  return ruolo?.ruolo ?? null;
}

function formatDate(date: Date | string | null | undefined, locale: string): string {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale === 'en' ? 'en-GB' : 'it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function UserManagementPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // State
  const [users, setUsers] = useState<Utente[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterRuolo, setFilterRuolo] = useState('');
  const [filterStato, setFilterStato] = useState('');

  // Add user dialog
  const [addUserOpen, setAddUserOpen] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<Utente | null>(null);
  const [editNome, setEditNome] = useState('');
  const [editCognome, setEditCognome] = useState('');
  const [editRuolo, setEditRuolo] = useState<Ruolo>('contributore');
  const [editStato, setEditStato] = useState(true);
  const [saving, setSaving] = useState(false);

  const aziendaId = useAuthStore(s => s.utente?.azienda?.id) || '';

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await userApi.list({
        search: search || undefined,
        ruolo: filterRuolo || undefined,
        stato: filterStato || undefined,
      });
      setUsers(result.users);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || i18n.t('settings.users.loadError', 'Errore nel caricamento utenti'));
    } finally {
      setLoading(false);
    }
  }, [search, filterRuolo, filterStato, i18n]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounce search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleOpenEdit = (utente: Utente) => {
    setEditUser(utente);
    setEditNome(utente.nome);
    setEditCognome(utente.cognome);
    setEditStato(utente.stato === 'attivo');
    const ruolo = getRuoloForAzienda(utente, aziendaId);
    setEditRuolo(ruolo || 'contributore');
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditUser(null);
  };

  const handleSave = async () => {
    if (!editUser) return;
    setSaving(true);
    setError(null);

    try {
      const currentRuolo = getRuoloForAzienda(editUser, aziendaId);
      const profileChanged =
        editNome !== editUser.nome ||
        editCognome !== editUser.cognome ||
        (editStato ? 'attivo' : 'inattivo') !== editUser.stato;
      const roleChanged = editRuolo !== currentRuolo;

      if (profileChanged) {
        await userApi.update(editUser._id, {
          nome: editNome,
          cognome: editCognome,
          stato: editStato ? 'attivo' : 'inattivo',
        });
      }

      if (roleChanged) {
        await userApi.updateRoles(editUser._id, {
          ruolo: editRuolo,
        });
      }

      handleCloseEdit();
      await fetchUsers();
    } catch (err: any) {
      setError(err.message || t('settings.users.saveError', 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
        {t('settings.users.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('settings.users.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Add User Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setAddUserOpen(true)}
        >
          {t('settings.users.addUser')}
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder={t('settings.users.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 280 }}
          inputProps={{ 'aria-label': t('settings.users.searchAriaLabel') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('settings.users.filterRole')}</InputLabel>
          <Select
            value={filterRuolo}
            label={t('settings.users.filterRole')}
            onChange={(e) => setFilterRuolo(e.target.value)}
          >
            <MenuItem value="">{t('settings.users.allRoles')}</MenuItem>
            {RUOLI.map((r) => (
              <MenuItem key={r} value={r}>
                {t(`settings.users.roles.${r}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('settings.users.filterStatus')}</InputLabel>
          <Select
            value={filterStato}
            label={t('settings.users.filterStatus')}
            onChange={(e) => setFilterStato(e.target.value)}
          >
            <MenuItem value="">{t('settings.users.allStatuses')}</MenuItem>
            <MenuItem value="attivo">{t('settings.users.attivo')}</MenuItem>
            <MenuItem value="inattivo">{t('settings.users.inattivo')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('settings.users.cognome')}</TableCell>
              <TableCell>{t('settings.users.nome')}</TableCell>
              <TableCell>{t('settings.users.email')}</TableCell>
              <TableCell>{t('settings.users.ruolo')}</TableCell>
              <TableCell>{t('settings.users.stato')}</TableCell>
              <TableCell>{t('settings.users.ultimoAccesso')}</TableCell>
              <TableCell align="center">{t('settings.users.azioni')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('settings.users.noUsers')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => {
                const ruolo = getRuoloForAzienda(u, aziendaId);
                return (
                  <TableRow key={u._id} hover>
                    <TableCell>{u.cognome}</TableCell>
                    <TableCell>{u.nome}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {ruolo ? (
                        <Chip
                          label={t(`settings.users.roles.${ruolo}`, ruolo)}
                          size="small"
                          color={ruolo === 'amministratore' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      ) : (
                        <Chip label="—" size="small" variant="outlined" sx={{ color: 'text.disabled', borderColor: 'divider' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={u.stato === 'attivo' ? t('settings.users.attivo') : t('settings.users.inattivo')}
                        size="small"
                        color={u.stato === 'attivo' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(u.ultimoAccesso, lang)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenEdit(u)} aria-label={t('settings.users.editUserAria')}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && total > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {total} {t('settings.users.totalUsers')}
        </Typography>
      )}

      {/* Add User Dialog */}
      <AddUserDialog
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        onSuccess={() => fetchUsers()}
      />

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>{t('settings.users.editUser')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('settings.users.nome')}
              value={editNome}
              onChange={(e) => setEditNome(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label={t('settings.users.cognome')}
              value={editCognome}
              onChange={(e) => setEditCognome(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label={t('settings.users.email')}
              value={editUser?.email || ''}
              size="small"
              fullWidth
              disabled
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{t('settings.users.ruolo')}</InputLabel>
              <Select
                value={editRuolo}
                label={t('settings.users.ruolo')}
                onChange={(e) => setEditRuolo(e.target.value as Ruolo)}
              >
                {RUOLI.map((r) => (
                  <MenuItem key={r} value={r}>
                    {t(`settings.users.roles.${r}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={editStato}
                  onChange={(e) => setEditStato(e.target.checked)}
                />
              }
              label={t('settings.users.userActive')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !editNome.trim() || !editCognome.trim()}
          >
            {saving ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
